import { AlertTriangle, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

export type PlatformReplyComposerMode = "platformReply" | "followUp"

type PlatformReplyComposerMetaProps = {
  canSendPlatformReply: boolean
  canUseFollowUp: boolean
  isPollingRecovering: boolean
  replyDisabledReason?: string
  platformReplyCandidate: boolean
  composerMode: PlatformReplyComposerMode
  onComposerModeChange: (mode: PlatformReplyComposerMode) => void
  onOpenCrm: () => void
}

export const PlatformReplyComposerMeta = ({
  canSendPlatformReply,
  canUseFollowUp,
  isPollingRecovering,
  replyDisabledReason,
  platformReplyCandidate,
  composerMode,
  onComposerModeChange,
  onOpenCrm,
}: PlatformReplyComposerMetaProps) => {
  const { t } = useTranslation()
  const disabledCopyKey = getDisabledCopyKey(replyDisabledReason)

  if (canSendPlatformReply) {
    return (
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1 rounded-md bg-muted/40 p-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={cn(
                  "rounded px-2 py-1 font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                  composerMode === "platformReply"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
                onClick={() => onComposerModeChange("platformReply")}
              >
                {t("history.platformReply.composerTitle")}
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              className="max-w-xs text-left leading-relaxed"
            >
              {t("history.platformReply.sendViaOriginalChannel")}
            </TooltipContent>
          </Tooltip>
          {canUseFollowUp && (
            <button
              type="button"
              className={cn(
                "rounded px-2 py-1 font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                composerMode === "followUp"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
              onClick={() => onComposerModeChange("followUp")}
            >
              {t("history.platformReply.askAiMode")}
            </button>
          )}
        </div>
        <Button
          variant="link"
          size="sm"
          className="h-auto shrink-0 px-0 py-0 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground"
          onClick={onOpenCrm}
        >
          {t("history.platformReply.goToCrm")}
        </Button>
        {isPollingRecovering && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="inline-flex shrink-0 items-center gap-1.5 rounded-sm text-xs font-medium text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label={t("history.platformReply.polling.recovering")}
                aria-live="polite"
                role="status"
                tabIndex={0}
              >
                <Loader2
                  className="h-3.5 w-3.5 animate-spin"
                  aria-hidden="true"
                />
              </span>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              className="max-w-xs text-left leading-relaxed"
            >
              {t("history.platformReply.polling.recoveringDescription")}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    )
  }

  if (!platformReplyCandidate) return <div />

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <div className="inline-flex max-w-full items-center gap-1.5 text-xs text-muted-foreground">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="flex min-w-0 items-center gap-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              aria-label={t(
                `history.platformReply.disabled.${disabledCopyKey}.description`
              )}
            >
              <AlertTriangle
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70"
                aria-hidden="true"
              />
              <span className="truncate font-medium text-foreground/90">
                {t(`history.platformReply.disabled.${disabledCopyKey}.title`)}
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="max-w-xs text-left leading-relaxed"
          >
            {t(`history.platformReply.disabled.${disabledCopyKey}.description`)}
          </TooltipContent>
        </Tooltip>
        {replyDisabledReason === "not_human_mode" && (
          <Button
            variant="link"
            size="sm"
            className="h-auto shrink-0 px-0 py-0 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground"
            onClick={onOpenCrm}
          >
            {t("history.platformReply.goToCrm")}
          </Button>
        )}
      </div>
    </div>
  )
}

const getDisabledCopyKey = (reason?: string) => {
  switch (reason) {
    case "not_human_mode":
      return "notHumanMode"
    case "unsupported_history_transcript":
      return "unsupportedHistory"
    case "missing_source_channel":
      return "missingSourceChannel"
    case "unsupported_channel":
      return "unsupportedChannel"
    case "permission_denied":
      return "permissionDenied"
    default:
      return "generic"
  }
}
